import { Button, Center, Link, Stack, Text } from "@chakra-ui/react";
import type {
  LoaderArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import { Link as RouterLink, redirect, useLoaderData } from "@remix-run/react";
import { withZod } from "@remix-validated-form/with-zod";
import { ValidatedForm } from "remix-validated-form";
import { z } from "zod";
import { IdentityApi } from "~/api/identity-api.ts";
import { T, t } from "~/libs/i18n";
import {
  formValidationError,
  ok,
  serverError,
} from "~/utils/response-handlers.ts";
import { SubmitButton } from "../../components/forms/form-buttons";
import {
  ValidatedInput,
  ValidatedTextarea,
} from "../../components/forms/validated-inputs";
import { PreventionLogo } from "../../components/prevention-logo";

export const meta: MetaFunction = () => {
  return [{ title: `${t("Contact us")}` }];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const { searchParams } = new URL(request.url);
  const success = searchParams.get("success");
  return ok(request, { data: { success: success == "true" } });
}

export default function ContactUs() {
  const loaderData = useLoaderData<typeof loader>();
  return (
    <Center>
      <Stack
        w={"full"}
        bg={"white"}
        spacing={8}
        p={{ base: 4, md: 8, lg: 16 }}
        alignContent={"center"}
        overflow={{ base: "auto", lg: "visible" }}
      >
        <PreventionLogo />
        {loaderData.data.success ? <ContactUsSent /> : <ContactUsForm />}
      </Stack>
    </Center>
  );
}

const formValidator = withZod(
  z.object({
    fromEmail: z.string().email().min(1, t("Enter your email address")),
    name: z.string().min(1, t("Name is required")),
    company: z.string().min(1, t("Company is required")),
    body: z.string().min(1, t("Message is required")),
  }),
);

function ContactUsForm() {
  return (
    <Stack>
      <Text textStyle={"heading"}>
        <T>Contact us</T>
      </Text>
      <ValidatedForm validator={formValidator} method={"post"}>
        <Stack>
          <ValidatedInput
            name={"fromEmail"}
            type={"email"}
            label={t("Email")}
          />
          <ValidatedInput name={"name"} type={"text"} label={t("Name")} />
          <ValidatedInput name={"company"} type={"text"} label={t("Company")} />
          <ValidatedTextarea
            name={"body"}
            label={t("Message")}
            minHeight="10em"
          />

          <Stack
            alignItems={"center"}
            spacing={4}
            px={{ sm: 2, md: 12 }}
            py={4}
          >
            <SubmitButton>
              <T>Submit</T>
            </SubmitButton>
            <Button as={RouterLink} variant={"ghost"} to={"/login"}>
              <T>Cancel</T>
            </Button>
          </Stack>
        </Stack>
      </ValidatedForm>
    </Stack>
  );
}

function ContactUsSent() {
  return (
    <Stack>
      <Text>Your request has been sent, we'll get back to you.</Text>
      <Link href={"/"}>
        <T>Go back to login</T>
      </Link>
    </Stack>
  );
}

export async function action({ request }: LoaderArgs) {
  const validatedForm = await formValidator.validate(await request.formData());
  if (validatedForm.error) {
    return formValidationError({
      fieldErrors: validatedForm.error.fieldErrors,
    });
  }

  const { fromEmail, name, company, body } = validatedForm.data;
  const response = await IdentityApi.SendContactUsEmail(request, {
    fromEmail,
    name,
    company,
    body,
  });
  if (!response.ok) {
    return await serverError({ error: response.error });
  }

  return redirect("?success=true");
}
